import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";
import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FloatingHeroItem } from "../components/floating-hero-item.tsx";
import { PropertyImageGallery } from "./components/property-image-gallery.tsx";
import { TabsList, TabsTrigger } from "../../../components/base/tabs.tsx";
import { cn } from "../../../utils/cn.ts";
import { Button } from "../../../components/buttons/button.tsx";
import { CachingTabsView, type TabDefinition } from "../components/caching-tabs-view.tsx";
import type { TabId } from "../types/tab-id.ts";
import { GalleryModal } from "../gallery-modal/gallery-modal.tsx";
import { getPropertyDetailsTabsMeta } from "../utils/property-details-tab-definitions.ts";
import { PropertyMap } from "../components/property-map.tsx";
import { PropertyStreetView } from "../components/property-street-view.tsx";
import { VirtualTour } from "../components/virtual-tour.tsx";
import { FloorPlanProxy } from "../components/floor-plan-proxy.tsx";
import { baseFocusStyle } from "../../../components/buttons/button-utility.ts";
import { PropertyVideo } from "../components/property-video.tsx";
import { usePropertyDetailsContext } from "../../contexts/property-details-context.tsx";

export const PropertyDetailsHero: React.FC = () => {
  const {
    images = [],
    virtualTourUrl,
    isVirtualTourExternalRedirect = false,
    isVideoExternalRedirect = false,
    videoUrl,
    floorPlanResourceUrl,
    displayAddress,
    lat: rawLat,
    lon: rawLon,
    propertyReference,
    propertySource,
  } = usePropertyDetailsContext();
  const lat = rawLat ?? 0;
  const lon = rawLon ?? 0;
  const hasLocation = rawLat != null && rawLon != null;

  const [activeTab, setActiveTab] = useState<TabId>("photos");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const tabsMeta = getPropertyDetailsTabsMeta(images);
  const TABS: TabDefinition[] = [
    {
      ...tabsMeta.photos,
      includeTab: images.length > 0,
      content: <PropertyImageGallery />,
    },
    {
      ...tabsMeta.virtualTour,
      includeTab: !!virtualTourUrl,
      content: <VirtualTour url={virtualTourUrl} isVirtualTourExternalRedirect={isVirtualTourExternalRedirect} />,
    },
    {
      ...tabsMeta.video,
      includeTab: !!videoUrl,
      skipClickReporting: isVideoExternalRedirect,
      content: <PropertyVideo url={videoUrl} isVideoExternalRedirect={isVideoExternalRedirect} />,
    },
    {
      ...tabsMeta.floorPlan,
      includeTab: !!floorPlanResourceUrl,
      content: floorPlanResourceUrl ? <FloorPlanProxy floorPlanResourceUrl={floorPlanResourceUrl} /> : null,
    },
    {
      ...tabsMeta.map,
      includeTab: hasLocation,
      content: (
        <PropertyMap lat={lat} lon={lon} displayAddress={displayAddress ?? ""} isTabActive={activeTab === "map"} />
      ),
    },
    {
      ...tabsMeta.streetView,
      includeTab: hasLocation,
      content: <PropertyStreetView lat={lat} lon={lon} isTabActive={activeTab === "street-view"} />,
    },
  ];

  useEffect(() => {
    const firstVisibleTab = TABS.find((x) => x.includeTab);
    if (firstVisibleTab) {
      setActiveTab(firstVisibleTab.id);
    }
  }, []);

  return (
    <DialogPrimitive.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
      <CachingTabsView
        tabs={TABS}
        tabsListPosition="bottom"
        className="flex flex-col gap-5 md:gap-4.5"
        contentClassName="aspect-[360/245] md:aspect-[1200/517] relative overflow-hidden"
        tabsContentClassName="h-full"
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        reportingPropertyReference={propertyReference}
        reportingPropertySource={propertySource}
        tabsList={
          <TabsList className="gap-2.5 md:gap-3 mx-3.5 md:mx-0" aria-label="Gallery tabs">
            {TABS.filter((tab) => tab.includeTab).map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <TabsTrigger key={tab.id} value={tab.id} asChild>
                  <Button
                    colorVariant={isActive ? "secondary" : "accent"}
                    variant={isActive ? "solid" : "outlined"}
                    label={tab.label}
                    labelStyle={cn("hidden min-[990px]:block", "leading-none")}
                    renderLeadingIcon={() => (
                      <i
                        className={cn(
                          `fa-${tab.icon} fa-sharp fa-regular`,
                          isActive ? "text-neutral-white" : "text-loch",
                          "text-body-xl min-[990px]:text-body-md",
                        )}
                        aria-hidden="true"
                      />
                    )}
                    className={cn("flex-1", "px-1 py-1", isActive ? "border-2 border-loch" : "")}
                  />
                </TabsTrigger>
              );
            })}
          </TabsList>
        }
      >
        {activeTab != "virtual-tour" && activeTab != "video" && (
          <DialogPrimitive.Trigger asChild>
            <button
              type="button"
              aria-label="Open Gallery"
              className={cn(
                baseFocusStyle,
                "absolute bottom-2.5 right-2.5 md:right-5 md:bottom-5 z-[999] group rounded  focus:shadow-north-sea-100 ",
              )}
            >
              <FloatingHeroItem iconName="expand-wide" label="Open in gallery" mobileLabel="Gallery" />
            </button>
          </DialogPrimitive.Trigger>
        )}
      </CachingTabsView>
      <GalleryModal heroActiveTabId={activeTab} isOpen={isModalOpen} />
    </DialogPrimitive.Root>
  );
};
